import { UnstyledButton } from "@mantine/core";
import { ChevronRight, FolderOpen } from "lucide-react";
import { useRef, useState, type PointerEvent, type ReactNode } from "react";

import classes from "./ProjectsLayout.module.css";

/** Live's sidebar (`MIN_SIDEBAR_WIDTH`): its width when it opens, and the least it narrows to. */
export const PANEL_MIN_WIDTH = 240;
/** Live's header section while the panel is closed (`CLOSED_SECTION_WIDTH`). */
const CLOSED_WIDTH = 130;

/**
 * Live's `useResizable`: the panel's width, dragged from its edge, never under the minimum and
 * with no maximum. As live's, the edge is the pointer's alone: the panel works at any width.
 */
function usePanelWidth() {
  const [width, setWidth] = useState(PANEL_MIN_WIDTH);
  const [resizing, setResizing] = useState(false);
  const start = useRef({ x: 0, width: PANEL_MIN_WIDTH });
  const widen = (next: number) => setWidth(Math.max(PANEL_MIN_WIDTH, Math.round(next)));
  const edge = {
    onPointerDown: (event: PointerEvent<HTMLHRElement>) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      start.current = { x: event.clientX, width };
      setResizing(true);
    },
    onPointerMove: (event: PointerEvent<HTMLHRElement>) => {
      if (resizing) widen(start.current.width + event.clientX - start.current.x);
    },
    onPointerUp: () => setResizing(false),
    onPointerCancel: () => setResizing(false),
  };
  return { width, resizing, edge };
}

export interface ProjectsLayoutProps {
  open: boolean;
  onToggle: () => void;
  /** Beside "Projects" while the panel is open (live's favourites star). */
  headerSlot: ReactNode;
  /** The rest of the header row: the library's filters and settings. */
  header: ReactNode;
  /** The panel itself: All assets, the projects' tree and Create Project. */
  panel: ReactNode;
  /** The assets, which scroll on their own. */
  children: ReactNode;
}

/**
 * Live's `ProjectsSidebarLayout` (#784): one header row, "Projects" (and its star while open) as
 * wide as the panel, then the filters; under it the panel and the assets side by side, each
 * scrolling on its own, the page never. The panel starts closed, opens to 240 px and widens from
 * its edge; its line runs the full height once it is open. It stays mounted while closed, as
 * live's, out of reach (inert), so it slides rather than appears.
 */
export function ProjectsLayout({ open, onToggle, headerSlot, header, panel, children }: ProjectsLayoutProps) {
  const { width, resizing, edge } = usePanelWidth();
  const moving = resizing ? true : undefined;
  return (
    <div className={classes.wrapper}>
      <div className={classes.line} data-visible={open || undefined} style={{ left: width - 1 }} aria-hidden />
      <div className={classes.header}>
        <div className={classes.section} data-resizing={moving} style={{ width: open ? width : CLOSED_WIDTH }}>
          <UnstyledButton className={classes.projects} aria-expanded={open} onClick={onToggle}>
            <FolderOpen size={16} strokeWidth={1.5} className={classes.projectsIcon} aria-hidden />
            {/* Live's word for a brand's folders. */}
            <span className={classes.label}>Projects</span>
          </UnstyledButton>
          {open ? <div className={classes.slot}>{headerSlot}</div> : null}
          <UnstyledButton
            className={classes.chevronButton}
            aria-label={open ? "Close the projects" : "Open the projects"}
            aria-expanded={open}
            onClick={onToggle}
          >
            <ChevronRight size={16} strokeWidth={1.5} className={classes.chevron} aria-hidden />
          </UnstyledButton>
        </div>
        <div className={classes.headerContent}>{header}</div>
      </div>
      <div className={classes.body}>
        <div
          className={classes.panel}
          data-resizing={moving}
          style={{ width: open ? width : 0 }}
          inert={!open}
          aria-hidden={open ? undefined : true}
        >
          <div className={classes.panelInner}>{panel}</div>
          {open ? (
            <hr className={classes.edge} aria-hidden {...edge} />
          ) : null}
        </div>
        <div className={classes.content} data-open={open || undefined}>
          {children}
        </div>
      </div>
    </div>
  );
}
